import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DecisivePlayerPickerMode,
  DecisivePlayerPickerTaskAppearanceStatus,
  GameStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { iconPen } from '@sorare/core/src/atoms/icons/system/iconPen.ts';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelXS } from '@sorare/core/src/atoms/typography';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { IMAGES, StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { isType } from '@sorare/core/src/lib/gql';
import type { Stats } from '@sorare/core/src/lib/scoring';
import { statLabels } from '@sorare/core/src/lib/scoring';
import type { Color } from '@sorare/core/src/style/types';
import {
  statToLabel,
  statToValue,
} from '@sorare/marketplace/src/shared/stats/PlayerGameScoreStats/utils.ts';
import { css } from '@sorare/css';

import { BaseSlot } from '#src/components/missions/picker/BaseSlot/index.tsx';

import type {
  DecisivePickerSlot_card,
  DecisivePickerSlot_taskAppearance,
} from './__generated__/index.graphql.ts';

const action = css`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(30%, -30%);
  font-size: var(--fs-10);
  border-radius: 50%;
  background-color: var(--action-bg-color, var(--c-white));
  color: var(--action-color, var(--c-black));
  padding: 2px;

  button:disabled & {
    display: none;
  }
`;
const giftCircle = css`
  transform: translate(25%, -25%);
  padding: var(--half-unit);
`;

const labelXS = css`
  padding: 2px var(--half-unit);
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

const statCountLabel = css`
  padding: 0 2px;
  font-weight: bold;
`;

export const DecisivePickerSlot = ({
  taskAppearance,
  card,
  isSelected,
  showLockedOverlay,
  index,
  showAction,
  mode,
}: {
  taskAppearance?: DecisivePickerSlot_taskAppearance;
  card?: DecisivePickerSlot_card;
  isSelected?: boolean;
  showLockedOverlay?: boolean;
  index: number;
  showAction?: boolean;
  mode: DecisivePlayerPickerMode;
}) => {
  const cardToDisplay = taskAppearance?.card || card;

  if (taskAppearance) {
    const { playerGameScore, locked } = taskAppearance;

    const gameStatus = playerGameScore?.anyGame?.statusTyped;

    const hasSucceed =
      isType(taskAppearance, 'DecisivePlayerPickerTaskAppearance') &&
      taskAppearance.status ===
        DecisivePlayerPickerTaskAppearanceStatus.SUCCESS;

    const positiveDecisiveStats = isType(playerGameScore, 'PlayerGameScore')
      ? playerGameScore?.positiveDecisiveStats
      : undefined;

    const hasFailed =
      isType(taskAppearance, 'DecisivePlayerPickerTaskAppearance') &&
      taskAppearance.status ===
        DecisivePlayerPickerTaskAppearanceStatus.FAILURE;

    const reachedStatThreshold = isType(
      taskAppearance,
      'DecisivePlayerPickerTaskAppearance'
    )
      ? taskAppearance.reachedStatThreshold
      : undefined;

    const statThresholdColor = reachedStatThreshold?.color as Color;

    if (
      mode === DecisivePlayerPickerMode.SCORE &&
      taskAppearance.playerGameScore
    ) {
      if (hasFailed || hasSucceed || gameStatus === GameStatus.playing) {
        let color: Color = 'var(--c-nd-150)';
        if (statThresholdColor) {
          color = statThresholdColor;
        } else if (hasFailed) {
          color = 'var(--c-rewards-orange)';
        } else if (hasSucceed) {
          color = 'var(--c-onBase-success)';
        }
        return (
          <BaseSlot
            label={
              <Horizontal gap={0}>
                {gameStatus === GameStatus.playing && <LiveDot animate />}
                <Score
                  size={3}
                  hideReviewing
                  score={taskAppearance.playerGameScore.score}
                  scoreStatus={taskAppearance.playerGameScore.scoreStatus}
                  shape="PILL"
                  scoreType="PLAYER_SCORE"
                />
              </Horizontal>
            }
            showLockedOverlay={showLockedOverlay}
            color={color}
            card={cardToDisplay}
          />
        );
      }
    } else if (mode === DecisivePlayerPickerMode.DECISIVE) {
      if (hasSucceed) {
        const statKey =
          mode === DecisivePlayerPickerMode.DECISIVE &&
          positiveDecisiveStats?.find(stat => stat.statValue > 0)?.stat;

        const chipIconOrText = (() => {
          const taskThresholds = isType(
            taskAppearance.task,
            'DecisivePlayerPickerTask'
          )
            ? taskAppearance.task.statThresholds || []
            : [];
          if (reachedStatThreshold) {
            const allSameStat =
              taskThresholds.length > 0 &&
              taskThresholds?.every(t => t.stat === taskThresholds.at(0)?.stat);
            const statLabel = statToLabel(reachedStatThreshold.stat, true);
            const statLabelNode =
              typeof statLabel === 'string' ? (
                statLabel
              ) : (
                <FormattedMessage {...statLabel} />
              );
            return (
              <span className={statCountLabel}>
                {statToValue(
                  reachedStatThreshold.stat,
                  reachedStatThreshold.min
                )}
                {!allSameStat && <>&nbsp;{statLabelNode}</>}
              </span>
            );
          }
          if (statKey && statKey in statLabels && statKey in IMAGES) {
            return <StatIcon statKey={statKey as Stats} />;
          }
          return <FontAwesomeIcon icon={iconCheck} />;
        })();
        return (
          <BaseSlot
            icon={
              <LabelXS color="var(--c-onBase-light)" bold>
                {chipIconOrText}
              </LabelXS>
            }
            showLockedOverlay={showLockedOverlay}
            color={statThresholdColor || 'var(--c-onBase-success)'}
            card={cardToDisplay}
          >
            {!!showAction && (
              <Horizontal
                className={classNames(action, giftCircle)}
                gap={0}
                center
              >
                <Gift size={1.5} />
              </Horizontal>
            )}
          </BaseSlot>
        );
      }

      if (hasFailed) {
        return (
          <BaseSlot
            icon={
              <LabelXS color="var(--c-onBase-light)">
                <FontAwesomeIcon icon={iconX} />
              </LabelXS>
            }
            showLockedOverlay={showLockedOverlay}
            color="var(--c-rewards-orange)"
            card={cardToDisplay}
          />
        );
      }

      if (gameStatus === GameStatus.playing) {
        return (
          <BaseSlot
            icon={
              <LabelXS className={labelXS} color="var(--c-onBase-primary)">
                <LiveDot animate />
                <FontAwesomeIcon icon={iconHourglass} />
              </LabelXS>
            }
            showLockedOverlay={showLockedOverlay}
            color="var(--c-nd-150)"
            card={cardToDisplay}
          />
        );
      }
    }

    return (
      <BaseSlot
        icon={
          <LabelXS className={labelXS} color="var(--c-onBase-primary)">
            <FontAwesomeIcon icon={iconHourglass} />
          </LabelXS>
        }
        showLockedOverlay={showLockedOverlay}
        color={isSelected ? 'var(--c-blue-400)' : 'var(--c-nd-150)'}
        card={cardToDisplay}
      >
        {!locked && !!showAction && (
          <Horizontal
            className={action}
            gap={0}
            center
            style={
              {
                '--action-bg-color': 'var(--c-black)',
                '--action-color': 'var(--c-white)',
              } as CSSProperties
            }
          >
            <FontAwesomeIcon icon={iconPen} />
          </Horizontal>
        )}
      </BaseSlot>
    );
  }

  return (
    <BaseSlot
      icon={<BrandXXS>{index + 1}</BrandXXS>}
      showLockedOverlay={showLockedOverlay}
      color={isSelected ? 'var(--c-blue-400)' : 'var(--c-nd-150)'}
      card={cardToDisplay}
    >
      {!!showAction && (
        <Horizontal className={action} gap={0} center>
          <FontAwesomeIcon icon={iconPlus} />
        </Horizontal>
      )}
    </BaseSlot>
  );
};

DecisivePickerSlot.fragments = {
  taskAppearance: gql`
    fragment DecisivePickerSlot_taskAppearance on TaskAppearanceInterface {
      id
      index
      task {
        id
        ... on DecisivePlayerPickerTask {
          id
          mode
          statThresholds {
            stat
          }
        }
      }
      card {
        slug
        ...CardAvatar_anyCard
        ...BaseSlot_anyCardInterface
      }
      playerGameScore {
        id
        score
        scoreStatus
        anyGame {
          id
          statusTyped
        }
        ... on PlayerGameScore {
          scoreStatus
          id
          positiveDecisiveStats {
            stat
            statValue
          }
        }
      }
      locked
      ... on DecisivePlayerPickerTaskAppearance {
        id
        status
        reachedStatThreshold {
          color
          min
          stat
        }
      }
    }
    ${BaseSlot.fragments.anyCardInterface}
  ` as TypedDocumentNode<DecisivePickerSlot_taskAppearance>,
  card: gql`
    fragment DecisivePickerSlot_card on AnyCardInterface {
      slug
      ...BaseSlot_anyCardInterface
    }
    ${BaseSlot.fragments.anyCardInterface}
  ` as TypedDocumentNode<DecisivePickerSlot_card>,
};
